import { Row, Col, Card } from "antd";
import ChartTitle from "components/ChartTitle";
import Scorecard from "components/charts/Scorecard";
import TableChart from "components/charts/TableChart";
import { GRID_GUTTER } from "constants/ui";
import React, { useMemo } from "react";
import { useFormatNumber } from "hooks/useFormatNumber";
import { getDimensionColorByIndex } from "utils/colorUtils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const WizardDropoffSection = ({
  wizardStepData,
  wizardImpressions,
  wizardDismissals,
  wizardRegisterClicks,
  wizardDismissRate,
}) => {
  const formatNumber = useFormatNumber();

  // combine wizard steps + register as final funnel entry
  const funnelData = useMemo(() => {
    const steps = (wizardStepData || []).map((item) => {
      const isLastStep =
        wizardRegisterClicks != null &&
        item === wizardStepData[wizardStepData.length - 1];
      const dropoffRate = isLastStep
        ? item.count > 0
          ? parseFloat(
              (
                ((item.count - wizardRegisterClicks) / item.count) *
                100
              ).toFixed(1)
            )
          : null
        : item.dropoffRate;
      return { label: `Step ${item.step}`, count: item.count, dropoffRate };
    });

    if (wizardRegisterClicks != null) {
      steps.push({
        label: "Clicked Register",
        count: wizardRegisterClicks,
        dropoffRate: null,
      });
    }

    return steps;
  }, [wizardStepData, wizardRegisterClicks]);

  const tableColumns = useMemo(
    () => [
      {
        title: "Onboarding Step",
        dataIndex: "label",
        key: "label",
      },
      {
        title: "Users Reached This Step",
        dataIndex: "count",
        key: "count",
        align: "right",
        render: (value) => formatNumber(value),
      },
      {
        title: "Drop-off At This Step",
        dataIndex: "dropoffRate",
        key: "dropoffRate",
        align: "right",
        render: (value) => (value != null ? `${value}%` : "—"),
      },
    ],
    [formatNumber]
  );

  const hasData = Array.isArray(wizardStepData) && wizardStepData.length > 0;

  return (
    <>
      <Col span={24}>
        <ChartTitle>Onboarding Drop-off Rate by Tooltip Wizard Step</ChartTitle>
      </Col>

      {/* funnel bar chart */}
      <Col xs={24} lg={12} className="mb-8">
        <Card size="small">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={funnelData.filter((item) => item.dropoffRate != null)}
              margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => `${v}%`}
                domain={[0, 100]}
              />
              <Tooltip
                formatter={(value) => [`${value ?? 0}%`, "Drop-off Rate"]}
              />
              <Bar
                dataKey="dropoffRate"
                fill={getDimensionColorByIndex(0)}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      {/* scorecards */}
      <Col xs={24} lg={12} className="mb-8">
        <Row gutter={GRID_GUTTER}>
          <Col span={24} className="mb-8">
            <Card size="small">
              <Scorecard
                title="Onboarding Started"
                value={formatNumber(wizardImpressions)}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} className="mb-8">
            <Card size="small">
              <Scorecard
                title="Users Clicked Register"
                value={formatNumber(wizardRegisterClicks ?? 0)}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} className="mb-8">
            <Card size="small">
              <Scorecard
                title="Onboarding Dismissed"
                value={formatNumber(wizardDismissals ?? 0)}
              />
            </Card>
          </Col>
          <Col span={24} className="mb-8">
            <Card size="small">
              <Scorecard
                title="Onboarding Non-completion Rate"
                value={`${wizardDismissRate ?? 0}%`}
              />
            </Card>
          </Col>
        </Row>
      </Col>

      {/* per-step drop-off table */}
      {hasData && (
        <Col span={24} className="mb-8">
          <Card size="small">
            <TableChart
              rowKey="label"
              dataSource={funnelData}
              pagination={false}
              columns={tableColumns}
            />
          </Card>
        </Col>
      )}
    </>
  );
};

export default WizardDropoffSection;
